import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Loader2, MessageSquare } from "lucide-react";

type Channel = "inApp" | "email" | "push";

interface MessengerSettings {
  enabled: boolean;
  smsFallbackEnabled: boolean;
  notifyOnNewMessage: boolean;
}

function MessagesSettingsCard() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<MessengerSettings>({
    queryKey: ["/api/messenger/settings"],
  });

  const save = useMutation({
    mutationFn: (patch: Partial<MessengerSettings>) =>
      apiRequest("PATCH", "/api/messenger/settings", patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/messenger/settings"] });
      qc.invalidateQueries({ queryKey: ["/api/messenger/conversations"] });
    },
    onError: (e: any) =>
      toast({ title: "Failed to save", description: e?.message, variant: "destructive" }),
  });

  const enabled = data?.enabled ?? false;

  return (
    <Card data-testid="card-messages-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Messages
        </CardTitle>
        <CardDescription>
          Direct messaging with your workspace. When on, teammates can reach you instantly and
          for free inside Clarik; otherwise they reach you by text.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <>
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-medium">Turn on Messages</div>
                <div className="text-xs text-muted-foreground">
                  Off by default. When on, you appear in the messaging directory and receive
                  messages free inside Clarik.
                </div>
              </div>
              <Switch
                checked={enabled}
                onCheckedChange={(v) => save.mutate({ enabled: v })}
                disabled={save.isPending}
                data-testid="switch-messenger-enabled"
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-medium">Text fallback</div>
                <div className="text-xs text-muted-foreground">
                  When someone you message can't be reached inside Clarik, send it as a text
                  message instead.
                </div>
              </div>
              <Switch
                checked={data?.smsFallbackEnabled ?? true}
                onCheckedChange={(v) => save.mutate({ smsFallbackEnabled: v })}
                disabled={save.isPending || !enabled}
                data-testid="switch-messenger-sms-fallback"
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-medium">Notify on new messages</div>
                <div className="text-xs text-muted-foreground">
                  Get an in-app notification when you receive a new message.
                </div>
              </div>
              <Switch
                checked={data?.notifyOnNewMessage ?? true}
                onCheckedChange={(v) => save.mutate({ notifyOnNewMessage: v })}
                disabled={save.isPending || !enabled}
                data-testid="switch-messenger-notify"
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

interface CategoryDef {
  key: string;
  group: string;
  label: string;
  description: string;
  audience: "user" | "admin";
  defaults: Record<Channel, boolean>;
}

interface CategoriesResp {
  categories: CategoryDef[];
}

interface PrefRow {
  category: string;
  inApp: boolean;
  email: boolean;
  push: boolean;
}

interface PrefsResp {
  userPreferences: PrefRow[];
  workspaceDefaults: PrefRow[];
}

export default function NotificationSettingsPage() {
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: catsResp, isLoading: catsLoading } = useQuery<CategoriesResp>({
    queryKey: ["/api/notifications/categories"],
  });

  const { data: prefsResp, isLoading: prefsLoading } = useQuery<PrefsResp>({
    queryKey: ["/api/notification-preferences"],
  });

  const [prefs, setPrefs] = useState<Record<string, Record<Channel, boolean>>>({});

  // Map workspace defaults so we can fall back when a user has no row.
  const wsDefaultsMap = useMemo(() => {
    const map: Record<string, Record<Channel, boolean>> = {};
    for (const r of prefsResp?.workspaceDefaults || []) {
      map[r.category] = { inApp: r.inApp, email: r.email, push: r.push };
    }
    return map;
  }, [prefsResp]);

  useEffect(() => {
    if (prefsResp) {
      const map: Record<string, Record<Channel, boolean>> = {};
      for (const r of prefsResp.userPreferences || []) {
        map[r.category] = { inApp: r.inApp, email: r.email, push: r.push };
      }
      setPrefs(map);
    }
  }, [prefsResp]);

  const save = useMutation({
    mutationFn: () =>
      apiRequest("PUT", "/api/notification-preferences", {
        preferences: prefs,
      }),
    onSuccess: () => {
      toast({ title: "Notification preferences saved" });
      qc.invalidateQueries({ queryKey: ["/api/notification-preferences"] });
    },
    onError: (e: any) =>
      toast({ title: "Failed to save", description: e?.message, variant: "destructive" }),
  });

  const getValue = (cat: CategoryDef, channel: Channel): boolean => {
    if (prefs[cat.key] && typeof prefs[cat.key][channel] === "boolean") {
      return prefs[cat.key][channel];
    }
    if (wsDefaultsMap[cat.key]) {
      return wsDefaultsMap[cat.key][channel];
    }
    return cat.defaults[channel];
  };

  const toggle = (cat: CategoryDef, channel: Channel, value: boolean) => {
    setPrefs((p) => {
      const fallback = wsDefaultsMap[cat.key] || cat.defaults;
      const current = p[cat.key] || { ...fallback };
      return { ...p, [cat.key]: { ...current, [channel]: value } };
    });
  };

  const grouped = useMemo(() => {
    const map: Record<string, CategoryDef[]> = {};
    for (const c of catsResp?.categories || []) {
      (map[c.group] ||= []).push(c);
    }
    return map;
  }, [catsResp]);

  if (catsLoading || prefsLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-4xl space-y-6">

      <MessagesSettingsCard />

      {Object.entries(grouped).map(([group, cats]) => (
        <Card key={group} data-testid={`card-prefs-group-${group.replace(/\s+/g, "-").toLowerCase()}`}>
          <CardHeader>
            <CardTitle>{group}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 gap-y-3 items-center">
              <div className="text-xs font-semibold text-muted-foreground">Event</div>
              <div className="text-xs font-semibold text-muted-foreground text-center w-16">In-app</div>
              <div className="text-xs font-semibold text-muted-foreground text-center w-16">Email</div>
              <div className="text-xs font-semibold text-muted-foreground text-center w-16">Push</div>
              {cats.map((c) => (
                <div key={c.key} className="contents">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{c.label}</div>
                    <div className="text-xs text-muted-foreground">{c.description}</div>
                  </div>
                  <div className="flex justify-center">
                    <Switch
                      checked={getValue(c, "inApp")}
                      onCheckedChange={(v) => toggle(c, "inApp", v)}
                      data-testid={`switch-${c.key}-inApp`}
                    />
                  </div>
                  <div className="flex justify-center">
                    <Switch
                      checked={getValue(c, "email")}
                      onCheckedChange={(v) => toggle(c, "email", v)}
                      data-testid={`switch-${c.key}-email`}
                    />
                  </div>
                  <div className="flex justify-center">
                    <Switch
                      checked={getValue(c, "push")}
                      onCheckedChange={(v) => toggle(c, "push", v)}
                      data-testid={`switch-${c.key}-push`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      <div className="flex justify-end sticky bottom-4">
        <Button
          size="lg"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          data-testid="button-save-prefs"
        >
          {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Save preferences
        </Button>
      </div>
    </div>
  );
}
